import { useState, useCallback } from "react";
import { ApolloError, useLazyQuery } from "@apollo/client";
import { useRecoilState, useSetRecoilState } from "recoil";
import countryList from "react-select-country-list";

import {
  QUERY_PROFILE,
  QUERY_PROFILE_BY_USERNAME,
} from "../../../GraphQL/queryUserProfile";
import { FOLLOWERS_QUERY } from "../../../GraphQL/followers";
import { AllstarUser, Session } from "../../../State";
import { PinnedClipsAtom } from "../../../State/clip";
import { getAvatarImage } from "../../../Utilities/User";

export const useUserProfile = (
  searchFor?: string,
  isUsernameQuery?: boolean,
) => {
  const [allstarUser, setAllstarUser] = useRecoilState(AllstarUser);
  const [session, setSession] = useRecoilState(Session);
  const setPinnedClips = useSetRecoilState(PinnedClipsAtom);

  const [user, setUser] = useState<any>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<ApolloError | null>(null);
  const [fatalError, setFatalError] = useState<any>(false);

  const [getUserProfile, { loading: loadingOld }] = useLazyQuery(
    QUERY_PROFILE,
    {
      onError: setError,
    },
  );

  const [getUserByUsername, { loading: loadingNew }] = useLazyQuery(
    QUERY_PROFILE_BY_USERNAME,
    {
      onError: setError,
    },
  );

  const [getFollowers] = useLazyQuery(FOLLOWERS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const loading = loadingOld || loadingNew;

  const fetchUserProfile = useCallback(async () => {
    if (!searchFor && !allstarUser.loggedIn) {
      setFatalError([true, "/login"]);
      return null;
    }

    const existsInSession = session?.profiles?.find(
      (profile: { username: string | undefined; userId: string | undefined }) =>
        profile?.username === searchFor || profile?.userId === searchFor,
    );

    if (existsInSession) {
      setUser(existsInSession);
      setReady(true);

      if (!existsInSession.followers) {
        try {
          const { data: followersData } = await getFollowers({
            variables: {
              userId: existsInSession.userId,
              profiles: true,
              clips: false,
            },
          });

          const followingList = followersData?.myFollowing?.profiles || [];
          const userWithFollowers = {
            ...existsInSession,
            followers: followingList,
          };

          setUser(userWithFollowers);
        } catch (error) {
          return null;
        }
      }

      return existsInSession;
    }

    try {
      const userParam = searchFor || allstarUser?.user?._id;

      let data, userData, profileData;

      if (isUsernameQuery) {
        const result = await getUserByUsername({
          variables: { username: userParam },
        });
        data = result.data;
        userData = data?.user;

        if (!userData) {
          const fallbackResult = await getUserProfile({
            variables: { user: userParam },
          });
          data = fallbackResult.data;
          userData = data?.userProfile;

          if (userData) {
            profileData = {
              _id: userData?.user?._id,
              admin: userData?.user?.admin,
              avatar: userData?.user?.avatar,
              createdDate: userData?.user?.createdDate,
              profile: userData?.profile,
              rank: userData?.user?.rank,
              stats: userData?.stats,
              userGroup: userData?.user?.userGroup,
              username: userData?.user?.username,
              steam: userData?.user?.steam,
              tiktok: userData?.user?.tiktok,
              riot: userData?.user?.riot,
            };
          }
        } else {
          profileData = {
            _id: userData._id,
            admin: userData.admin,
            avatar: userData.avatar,
            createdDate: userData.createdDate,
            profile: userData.profile,
            rank: userData.rank,
            stats: {
              totalClips: userData.clipCount,
              totalViews: 0,
            },
            userGroup: userData.userGroup,
            username: userData.username,
            steam: userData.steam,
            tiktok: userData.tiktok,
            riot: userData.riot,
          };
        }
      } else {
        const result = await getUserProfile({
          variables: { user: userParam },
        });
        data = result.data;
        userData = data?.userProfile;

        if (userData) {
          profileData = {
            _id: userData?.user?._id,
            admin: userData?.user?.admin,
            avatar: userData?.user?.avatar,
            createdDate: userData?.user?.createdDate,
            profile: userData?.profile,
            rank: userData?.user?.rank,
            stats: userData?.stats,
            userGroup: userData?.user?.userGroup,
            username: userData?.user?.username,
            steam: userData?.user?.steam,
            tiktok: userData?.user?.tiktok,
            riot: userData?.user?.riot,
          };
        }
      }

      if (!userData || !profileData) {
        setFatalError([true, "/404"]);
        return null;
      }

      const isOwnProfile = profileData._id === allstarUser?.user?._id;

      const formattedUser = {
        avatarImage: getAvatarImage({
          _id: profileData._id,
          avatar: profileData.avatar,
        }),
        data: profileData,
        rank: profileData.rank,
        userGroup: profileData.userGroup,
        isOwnProfile,
        userId: profileData._id,
        username:
          profileData?.profile?.username || profileData?.username || null,
        socials: profileData.profile?.profile?.socials || null,
        displayName:
          profileData?.profile?.username ||
          profileData?.username ||
          "Allstar User",
        country: profileData.profile?.profile?.countryCode
          ? countryList().getLabel(profileData.profile.profile.countryCode)
          : "Planet Earth",
        joinDate: profileData.createdDate,
        stats: profileData.stats,
        loading: false,
        lastRun: Date.now(),
        steamId: profileData.steam?.steamid,
        riotId: profileData.riot?.puuid,
      };

      if (isOwnProfile) {
        setAllstarUser({
          ...allstarUser,
          ownProfilePage: formattedUser,
        });
      }

      setSession({
        ...session,
        profiles: [...session.profiles, formattedUser],
      });

      setPinnedClips(
        isUsernameQuery
          ? userData?.pinnedClips || []
          : userData?.user?.pinnedClips || [],
      );

      try {
        const { data: followersData } = await getFollowers({
          variables: { userId: profileData._id, profiles: true, clips: false },
        });

        const followingList = followersData?.myFollowing?.profiles || [];
        const userWithFollowers = {
          ...formattedUser,
          followers: followingList,
        };

        if (isOwnProfile) {
          setAllstarUser({
            ...allstarUser,
            followers: followingList,
            ownProfilePage: userWithFollowers,
          });
        }

        setUser(userWithFollowers);
      } catch (followersError) {
        setUser(formattedUser);
      }

      setReady(true);
      return formattedUser;
    } catch (err) {
      setError(err as ApolloError);
      return null;
    }
  }, [
    searchFor,
    isUsernameQuery,
    getUserByUsername,
    getUserProfile,
    getFollowers,
    setPinnedClips,
  ]);

  const clearProfile = () => {
    setUser(null);
    setReady(false);
    setError(null);
    setFatalError(false);
    setPinnedClips([]);
  };

  return {
    user,
    ready,
    loading,
    error,
    fatalError,
    fetchUserProfile,
    clearProfile,
  };
};
